import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import { deliveryAPI, SubscriptionCard as CardData } from '../../services/deliveryAPI';
import DeliveryOfferCard from './DeliveryOfferCard';

const RED = '#8B0000';

const STATE_CHIP: Record<CardData['state'], { label: string; color: string }> = {
  setup_incomplete: { label: 'Finish setup', color: '#b45309' },
  awaiting_launch: { label: 'On the first route', color: '#1d4ed8' },
  awaiting_activation: { label: 'Starting soon', color: '#1d4ed8' },
  open: { label: 'Open for changes', color: '#1d4ed8' },
  locked: { label: 'Confirmed', color: '#047857' },
  waiting: { label: 'Up next', color: '#6b7280' },
  today: { label: 'Today', color: '#047857' },
  en_route: { label: 'On the way', color: '#047857' },
  delivered: { label: 'Delivered', color: '#047857' },
  payment_failed: { label: 'Card declined', color: '#b91c1c' },
  paused: { label: 'Paused', color: '#6b7280' },
  cancelled: { label: 'Cancelled', color: '#6b7280' },
};

const fmtDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const stepper: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff',
  fontSize: 22, fontWeight: 700, cursor: 'pointer', lineHeight: 1,
};

/**
 * The delivery subscriber's home card, and - for a member without a subscription - the
 * offer to start one (DeliveryOfferCard).
 *
 * The subscriber's card: where their next delivery stands, one state at a
 * time (delivery/subscription_card.py). Inside the change window they change bag counts or
 * skip, under the same rules the office uses. The card on file is view-only. Live tracking
 * is not built, so "On the way" shows no position.
 */
const SubscriptionCard: React.FC = () => {
  const { member } = useBridgeClubAuth();
  const [data, setData] = useState<CardData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubscriber = !!member?.is_delivery_subscriber;

  useEffect(() => {
    if (!isSubscriber) return;
    let alive = true;
    deliveryAPI.getSubscription()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [isSubscriber]);

  // No subscription: offer one where we deliver, or say the Caldwell store is coming.
  // This used to be a bare `return null`, which is why delivery was undiscoverable to
  // every member who registered directly (DeliveryOfferCard).
  if (!isSubscriber) return <DeliveryOfferCard />;
  if (!data) return null;

  const run = async (action: () => Promise<CardData>) => {
    setBusy(true);
    setError(null);
    try {
      setData(await action());
    } catch (err: any) {
      setError(err?.error || err?.detail || "That didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const chip = STATE_CHIP[data.state];
  const order = data.order;

  const skip = () => {
    if (!order) return;
    if (!window.confirm(`Skip your ${fmtDay(order.delivery_date)} delivery? Nothing will be charged or delivered.`)) return;
    run(() => deliveryAPI.skipOrder(order.id));
  };

  const lines = order && order.lines.length > 0 && (
    <div style={{ margin: '10px 0' }}>
      {order.lines.map((line) => (
        <div key={line.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{line.label}</div>
          {data.state === 'open' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button style={stepper} disabled={busy} aria-label={`One fewer ${line.label}`}
                onClick={() => run(() => deliveryAPI.setOrderBagCount(order.id, line.id, line.bag_count - 1))}>−</button>
              <span style={{ minWidth: 26, textAlign: 'center', fontWeight: 700 }}>{line.bag_count}</span>
              <button style={stepper} disabled={busy} aria-label={`One more ${line.label}`}
                onClick={() => run(() => deliveryAPI.setOrderBagCount(order.id, line.id, line.bag_count + 1))}>+</button>
            </div>
          ) : (
            <span style={{ fontWeight: 700 }}>{line.bag_count} bag{line.bag_count === 1 ? '' : 's'}</span>
          )}
        </div>
      ))}
    </div>
  );

  let body: React.ReactNode = null;
  switch (data.state) {
    case 'setup_incomplete':
      body = (
        <>
          <p style={{ margin: '6px 0 12px' }}>
            Finish setting up your delivery{data.next_step_label ? `. Next: ${data.next_step_label.toLowerCase()}.` : '.'}
          </p>
          <Link to="/bridge-club/onboarding" style={{ display: 'inline-block', background: RED, color: '#fff', padding: '12px 20px', borderRadius: 8, fontWeight: 700, textDecoration: 'none' }}>
            Continue setup
          </Link>
        </>
      );
      break;
    case 'awaiting_launch':
    case 'awaiting_activation':
      // Delivery has not launched, or this customer is not in an activated batch yet
      // (delivery/activation.py). No order exists and none may, so the card says where
      // they stand rather than going silent while their bins draw down.
      body = (
        <p style={{ margin: '6px 0' }}>
          {data.launch_line || "You're set up. We'll be in touch when delivery starts."}
        </p>
      );
      break;
    case 'open':
      body = order && (
        <>
          <p style={{ margin: '6px 0 0', fontWeight: 600 }}>Here's what's coming {fmtDay(order.delivery_date)}.</p>
          {lines}
          <p style={{ margin: '0 0 10px', fontSize: 14, color: '#374151' }}>
            Change or skip by {fmtDay(order.window.last_day)}. After that we confirm your order and charge your card.
          </p>
          <button onClick={skip} disabled={busy}
            style={{ minHeight: 44, padding: '0 16px', borderRadius: 8, border: `1px solid ${RED}`, background: '#fff', color: RED, fontWeight: 600, cursor: 'pointer' }}>
            Skip this delivery
          </button>
        </>
      );
      break;
    case 'locked':
      body = order && (
        <>
          <p style={{ margin: '6px 0 0', fontWeight: 600 }}>Confirmed for {fmtDay(order.delivery_date)}.</p>
          {lines}
          {order.total && <p style={{ margin: 0, fontSize: 14 }}>Charged ${order.total}. We emailed you the details.</p>}
        </>
      );
      break;
    case 'waiting':
      if (order && order.status === 'skipped') {
        body = <p style={{ margin: '6px 0' }}>You skipped your {fmtDay(order.delivery_date)} delivery. We'll build your next one from your bins.</p>;
      } else if (order) {
        body = (
          <p style={{ margin: '6px 0' }}>
            Your next delivery is {fmtDay(order.delivery_date)}. You can change it from {fmtDay(order.window.opens)}, and we'll email you then.
          </p>
        );
      } else {
        body = <p style={{ margin: '6px 0' }}>We'll email you about ten days before your next delivery, when your bins are due for feed.</p>;
      }
      break;
    case 'today':
      body = <p style={{ margin: '6px 0' }}>Your delivery is on today's route.</p>;
      break;
    case 'en_route':
      body = <p style={{ margin: '6px 0' }}>Your delivery is on the way today.</p>;
      break;
    case 'delivered':
      body = <p style={{ margin: '6px 0' }}>Delivered{data.stop ? ` ${fmtDay(data.stop.delivery_date)}` : ''}. Thank you!</p>;
      break;
    case 'payment_failed':
      body = (
        <p style={{ margin: '6px 0' }}>
          Your card was declined for the {order ? fmtDay(order.delivery_date) : 'next'} delivery, so it isn't bought yet.
          Call us at <a href={`tel:${data.phone.replace(/[^0-9]/g, '')}`} style={{ color: RED, fontWeight: 700 }}>{data.phone}</a> and we'll sort it out.
        </p>
      );
      break;
    case 'paused':
      body = <p style={{ margin: '6px 0' }}>Your deliveries are paused. Call us at {data.phone} to start again.</p>;
      break;
    case 'cancelled':
      body = <p style={{ margin: '6px 0' }}>Your delivery subscription is cancelled. Call us at {data.phone} if you'd like to come back.</p>;
      break;
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '14px 16px', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 17, color: RED }}>Home delivery</h3>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: chip.color, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
          {chip.label}
        </span>
      </div>
      {error && <div style={{ background: '#FEF2F2', color: '#991B1B', borderRadius: 8, padding: '8px 10px', margin: '8px 0', fontSize: 14 }}>{error}</div>}
      {/* The service switch (delivery/pause.py): one line while every delivery is paused,
          whatever this customer's own state; it disappears on resume. */}
      {data.service_pause_line && (
        <p style={{ margin: '8px 0 4px', color: '#991B1B', fontWeight: 600 }}>{data.service_pause_line}</p>
      )}
      <div style={{ color: '#111827' }}>{body}</div>
      {/* THE WAY IN (Brad, 2026-09-18). This card used to be the whole subscription; the
          page behind it holds prices, the charge date, bins, preferences and history. One
          tap from home, no email needed. */}
      {data.state !== 'setup_incomplete' && (
        <Link to="/bridge-club/delivery"
          style={{ display: 'block', marginTop: 10, padding: '12px 14px', borderRadius: 8, background: RED, color: '#fff', fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}>
          View or change your subscription →
        </Link>
      )}
      {(data.card || data.tier_label) && data.state !== 'setup_incomplete' && data.state !== 'cancelled' && (
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 10, borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
          {data.tier_label && <span>{data.tier_label}</span>}
          {data.card && (
            <span>
              {data.tier_label ? ' · ' : ''}{data.card.card_type || 'Card'} ending {data.card.last_four}, exp {data.card.expiry}
              {data.card.expired && <strong style={{ color: '#b91c1c' }}> (expired, please update it in your profile)</strong>}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
